import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

export default function HeroCarousel({ movies }: { movies: Movie[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (movies.length <= 1 || paused) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % movies.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [movies.length, paused]);

  if (movies.length === 0) {
    return (
      <div className="h-[420px] md:h-[560px] flex items-center justify-center bg-background-200 text-foreground-500">
        暂无推荐内容
      </div>
    );
  }

  const go = (dir: number) => setIndex((i) => (i + dir + movies.length) % movies.length);
  const active = movies[index];

  return (
    <section
      className="relative w-full h-[480px] md:h-[600px] overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {movies.map((m, i) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={m.backdrop}
            alt={`${m.title} 剧照`}
            title={`${m.title} - 美国伦理电影`}
            className="w-full h-full object-cover"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30" />

      <div className="relative h-full w-full px-4 md:px-8 flex items-center">
        <div key={active.id} className="max-w-2xl animate-fade-in-up">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold mb-4">
            <i className="ri-award-line" /> 编辑精选 · {active.year}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-background-50 leading-tight mb-3">
            {active.title}
          </h1>
          <p className="text-sm md:text-base text-background-200/90 mb-2 tracking-wide">{active.originalTitle}</p>
          <div className="flex items-center gap-3 text-sm text-background-200/90 mb-5 flex-wrap">
            <span className="flex items-center gap-1 text-accent-400 font-semibold">
              <i className="ri-star-fill" /> {active.rating.toFixed(1)}
            </span>
            <span>{active.genres.join(' / ')}</span>
            <span>{active.duration} 分钟</span>
            <span>{active.region}</span>
          </div>
          <p className="hidden sm:block text-sm md:text-base leading-7 text-background-100/85 mb-7 line-clamp-3">
            {active.synopsis}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/detail/${active.id}`}
              className="px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看影片详情
            </Link>
            <Link
              to="/category"
              className="px-6 py-3 rounded-md bg-background-50/10 text-background-50 text-sm font-medium border border-background-50/40 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览全部影片
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 flex items-center gap-3">
        <button
          type="button"
          onClick={() => go(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 border border-background-50/30 hover:bg-background-50/25 transition-colors cursor-pointer"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 border border-background-50/30 hover:bg-background-50/25 transition-colors cursor-pointer"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="absolute bottom-7 left-4 md:left-8 flex items-center gap-2">
        {movies.map((m, i) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/50 hover:bg-background-50/80'}`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}